import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { flagEmoji } from "@/components/IpGeoChip";
import {
  GlobeIcon,
  WarningIcon,
  CopyIcon,
  CaretDownIcon,
  CaretRightIcon,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * AllIpsPanel — admin view of every IP that has ever touched any account.
 *
 * Each row groups all accounts associated with a unique IP (signup,
 * verification, last-seen, or admin-creator). Rows expand to show every
 * account on that IP. Supports filtering to "≥ 2 accounts" for
 * fraud-detection focus.
 *
 * Backend: GET /api/admin/ips → see routes_admin.py.
 */
export default function AllIpsPanel({ onPickIp }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [minAccounts, setMinAccounts] = useState(1);
  const [ispType, setIspType] = useState(null); // null = all
  const [expanded, setExpanded] = useState({});

  const load = async (min = minAccounts, isp = ispType) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        min_accounts: String(min),
        limit: "500",
      });
      if (isp) params.set("isp_type", isp);
      const { data } = await api.get(`/admin/ips?${params.toString()}`);
      setData(data);
    } catch (e) {
      toast.error("Failed to load IP list");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(1, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copy = (txt) => {
    navigator.clipboard.writeText(String(txt));
    toast.success(`Copied ${txt}`);
  };

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4" data-testid="admin-all-ips-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <h3 className="font-bold text-zinc-900 flex items-center gap-2">
            <GlobeIcon size={16} weight="fill" className="text-[#1D78AB]" />
            All IPs · grouped
          </h3>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            Every IP recorded across signup, ID verification, last-seen, and
            admin-created accounts. Click a row to see all accounts on that IP.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={minAccounts === 1 ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setMinAccounts(1);
              load(1, ispType);
            }}
            data-testid="admin-ips-filter-all"
          >
            All IPs
          </Button>
          <Button
            variant={minAccounts === 2 ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setMinAccounts(2);
              load(2, ispType);
            }}
            className={minAccounts === 2 ? "bg-amber-600 hover:bg-amber-700" : ""}
            data-testid="admin-ips-filter-multi"
          >
            <WarningIcon size={13} className="mr-1" />
            Multi-account only
          </Button>
        </div>
      </div>

      {/* ISP-type filter row — separates real fraud signals (residential
          IPs with ≥2 accounts) from noisy datacenter / mobile carrier NATs. */}
      <div className="flex items-center gap-2 flex-wrap mb-4 pb-3 border-b border-zinc-200">
        <span className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">
          ISP type:
        </span>
        {[
          { id: null,           label: "All",         tone: "" },
          { id: "residential",  label: "Residential", tone: "bg-rose-600 hover:bg-rose-700" },
          { id: "mobile",       label: "Mobile",      tone: "bg-sky-600 hover:bg-sky-700" },
          { id: "datacenter",   label: "Datacenter",  tone: "bg-zinc-700 hover:bg-zinc-800" },
          { id: "vpn",          label: "VPN / Tor",   tone: "bg-violet-700 hover:bg-violet-800" },
          { id: "unknown",      label: "Unknown",     tone: "bg-zinc-400 hover:bg-zinc-500" },
        ].map((opt) => {
          const active = ispType === opt.id;
          return (
            <Button
              key={String(opt.id)}
              variant={active ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setIspType(opt.id);
                load(minAccounts, opt.id);
              }}
              className={active && opt.tone ? opt.tone : ""}
              data-testid={`admin-ips-isp-${opt.id || "all"}`}
            >
              {opt.label}
            </Button>
          );
        })}
      </div>

      {loading && <div className="text-xs text-zinc-500">Loading…</div>}

      {!loading && data && (
        <>
          <div className="text-[11px] text-zinc-500 mb-2">
            Showing <strong>{data.returned}</strong> of {data.total} unique IPs
            {minAccounts > 1 && <> · filtered to ≥ {minAccounts} accounts</>}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">
                <tr className="border-b border-zinc-200">
                  <th className="text-left px-3 py-2 w-8"></th>
                  <th className="text-left px-3 py-2">IP · Location</th>
                  <th className="text-left px-3 py-2">Accounts</th>
                  <th className="text-left px-3 py-2">Kinds</th>
                  <th className="text-left px-3 py-2">First / Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.items.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-6 text-center text-xs text-zinc-500">
                      No IPs match this filter.
                    </td>
                  </tr>
                )}
                {data.items.map((row) => {
                  const isOpen = !!expanded[row.ip];
                  const flag = flagEmoji(row.ip_geo?.country_code);
                  const place = [row.ip_geo?.city, row.ip_geo?.country]
                    .filter(Boolean)
                    .join(", ");
                  const isMulti = row.account_count >= 2;
                  return (
                    <React.Fragment key={row.ip}>
                      <tr
                        className={`border-b border-zinc-100 cursor-pointer hover:bg-zinc-50 ${
                          isMulti ? "bg-amber-50/40" : ""
                        }`}
                        onClick={() =>
                          setExpanded((s) => ({ ...s, [row.ip]: !s[row.ip] }))
                        }
                        data-testid={`admin-ips-row-${row.ip.replace(/\./g, "-")}`}
                      >
                        <td className="px-3 py-2">
                          {isOpen ? (
                            <CaretDownIcon size={12} />
                          ) : (
                            <CaretRightIcon size={12} />
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            {flag && <span className="text-base leading-none">{flag}</span>}
                            <span className="text-zinc-700 text-[12px]">
                              {place || (row.ip_geo?.private ? "Private network" : "Unknown")}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              copy(row.ip);
                            }}
                            className="inline-flex items-center gap-1 text-[11px] font-mono text-zinc-500 hover:text-[#1D78AB] mt-0.5"
                          >
                            {row.ip}
                            <CopyIcon size={10} />
                          </button>
                          {row.ip_geo?.isp && (
                            <div className="text-[10px] text-zinc-400 mt-0.5">
                              ISP: {row.ip_geo.isp}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center justify-center min-w-[28px] h-6 px-2 rounded-full text-[11px] font-bold ${
                              isMulti
                                ? "bg-amber-500 text-white"
                                : "bg-zinc-200 text-zinc-700"
                            }`}
                          >
                            {row.account_count}
                          </span>
                          {onPickIp && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onPickIp(row.ip);
                              }}
                              className="ml-2 text-[10px] text-[#1D78AB] underline hover:no-underline"
                              data-testid={`admin-ips-search-${row.ip.replace(/\./g, "-")}`}
                            >
                              Open in Users
                            </button>
                          )}
                        </td>
                        <td className="px-3 py-2 text-[11px]">
                          <div className="flex flex-wrap gap-1">
                            {row.kinds.map((k) => (
                              <span
                                key={k}
                                className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold ${
                                  k === "signup"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : k === "verification"
                                    ? "bg-blue-100 text-blue-800"
                                    : k === "admin_created"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-zinc-100 text-zinc-700"
                                }`}
                              >
                                {k.replace("_", " ")}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-[11px] text-zinc-600 whitespace-nowrap">
                          <div>First: {fmt(row.first_seen_at)}</div>
                          <div className="text-zinc-400">Last: {fmt(row.last_seen_at)}</div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-zinc-50/60 border-b border-zinc-100">
                          <td colSpan={5} className="px-6 py-3">
                            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500 mb-2">
                              Accounts on this IP
                            </div>
                            <div className="space-y-1.5">
                              {row.users.map((u) => (
                                <div
                                  key={u.user_id}
                                  className="flex items-center gap-3 text-[11px]"
                                  data-testid={`admin-ips-account-${u.user_id}`}
                                >
                                  <span className="font-medium text-zinc-700 min-w-[200px] truncate">
                                    {u.name || u.email}
                                  </span>
                                  <span className="font-mono text-zinc-500 min-w-[200px] truncate">
                                    {u.email}
                                  </span>
                                  <span className="text-zinc-500">
                                    {u.account_type?.replace("_", " ")}
                                  </span>
                                  <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white border border-zinc-200 text-zinc-600">
                                    {u.kind?.replace("_", " ")}
                                  </span>
                                  {u.role === "admin" && (
                                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-600 text-white font-bold">
                                      ADMIN
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
